"use client"

import { useRef, useState, useEffect } from "react"
import { useInView } from "framer-motion"
import { useTranslations } from "@/lib/intl/use-translations"
import { AutoplayVideo } from "@/components/ui/autoplay-video"
import { ArrowRightIcon, Button, buttonVariants } from "@/components/ui/button"
import { CtaBanner } from "@/components/ui/cta-banner"
import { aboutKeys as k } from "@/i18n/about-keys"
import { jobSlug, type JobListing } from "@/lib/cms"
import Link from "next/link"
import Image from "next/image"
import { useLocalePath } from "@/lib/use-localized-navigation"
import {
  ActionPlanIcon,
  ChecklistIcon,
  ExerciseIcon,
  GoogleIcon,
  LifestyleIcon,
  LinkedInIcon,
  MapPinIcon,
  StarIcon,
  SwissShieldIcon,
} from "@/components/ui/icons"
import { blobMediaUrl } from "@/lib/blob-url"
import { splitIntoSentences } from "@/lib/split-paragraphs"

/* ───────────────────── CountUp ───────────────────── */

function CountUp({ target }: { target: number }) {
  const ref = useRef<HTMLSpanElement>(null)
  const isInView = useInView(ref, { once: true, amount: 0.3 })
  const [value, setValue] = useState(0)

  useEffect(() => {
    if (!isInView) return
    const duration = 1000
    const step = (timestamp: number, startTime?: number) => {
      const st = startTime ?? timestamp
      const progress = Math.min((timestamp - st) / duration, 1)
      const eased = 1 - Math.pow(1 - progress, 4)
      setValue(Math.round(eased * target))
      if (progress < 1) requestAnimationFrame((t) => step(t, st))
    }
    requestAnimationFrame((t) => step(t))
  }, [isInView, target])

  return <span ref={ref}>{value}</span>
}

/* ───────────────────── TeamPhoto ───────────────────── */

function TeamPhoto({ src, name }: { src?: string; name: string }) {
  const initials = name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
  if (src)
    return (
      <Image src={src} alt={name} fill className="object-cover object-top" />
    )
  return (
    <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-neutral-200 to-neutral-300">
      <span className="type-title-lg text-neutral-400">{initials}</span>
    </div>
  )
}

/* ───────────────────── CompanyLogo ───────────────────── */

const KNOWN_LOGOS: Record<string, { height: number; maxWidth: number }> = {
  "google.svg": { height: 12, maxWidth: 60 },
  "oxford.svg": { height: 12, maxWidth: 60 },
  "octorad.png": { height: 12, maxWidth: 60 },
  "kaia.svg": { height: 18, maxWidth: 90 },
  "coolblue.svg": { height: 12, maxWidth: 60 },
  "suitsupply.svg": { height: 12, maxWidth: 60 },
  "iqvia.svg": { height: 12, maxWidth: 60 },
  "uzh.svg": { height: 12, maxWidth: 60 },
  "eth.svg": { height: 12, maxWidth: 60 },
  "rtp-global.svg": { height: 12, maxWidth: 60 },
}

function CompanyLogo({ src, name }: { src: string; name: string }) {
  const filename = src.split("/").pop() || ""
  const logoConfig = KNOWN_LOGOS[filename]
  if (logoConfig) {
    return (
      <Image
        src={src}
        alt={name}
        width={logoConfig.maxWidth}
        height={logoConfig.height}
        className="w-auto object-contain object-left opacity-40 grayscale"
        style={{ height: logoConfig.height, maxWidth: logoConfig.maxWidth }}
      />
    )
  }
  return (
    <span className="type-body-sm font-medium tracking-wider text-neutral-300 uppercase">
      {name}
    </span>
  )
}

/* ───────────────────── Props ───────────────────── */

interface AboutClientProps {
  jobs: JobListing[]
}

/* ───────────────────── Component ───────────────────── */

const ANGELS: {
  name: string
  affiliation: string
  affiliationKey?: keyof typeof k
}[] = [
  { name: "Cody Gakpo", affiliation: "Liverpool FC" },
  {
    name: "Thomas Wolf",
    affiliation: "Hugging Face co-founder",
    affiliationKey: "angelHuggingFaceAffiliation",
  },
  { name: "Michiel Bakker", affiliation: "DeepMind" },
  { name: "Stef van Grieken", affiliation: "Cradle" },
  { name: "Kai Eberhardt", affiliation: "Oviva" },
  { name: "Sara Huerlimann", affiliation: "Zahnarztzentrum" },
]

/* Jobs — CMS is source of truth */

export function AboutClient({ jobs }: AboutClientProps) {
  const t = useTranslations("about")
  const tCareers = useTranslations("careers")
  const lp = useLocalePath()

  const translateJobType = (type: string) =>
    type === "Full-time"
      ? tCareers("jobTypeFullTime")
      : type === "Part-time"
        ? tCareers("jobTypePartTime")
        : type

  return (
    <>
      {/* ── Hero ────────────────────────────────────────── */}
      <section className="relative flex min-h-[85vh] items-center justify-center overflow-hidden pt-[72px]">
        {/* Gradient fallback (always visible behind video) */}
        <div className="from-aubergine-900 absolute inset-0 bg-gradient-to-br via-[#1a1a2e] to-[#0f0f23]" />{" "}
        {/* ds-color-ignore: gradient stop */}
        {/* Video — desktop only */}
        <div className="absolute inset-0 hidden md:block">
          <AutoplayVideo
            src={blobMediaUrl("/videos/about-hero.mp4")}
            className="absolute inset-0 h-full w-full object-cover"
          />
        </div>
        {/* Dark gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70" />
        {/* Content */}
        <div className="relative z-10 mx-auto max-w-[1440px] px-4 text-center md:px-10">
          <div className="mb-6 flex justify-center">
            <a
              href="https://www.google.com/maps/place/Ahead+Health/@47.3734617,8.5311033,17z/data=!4m16!1m9!3m8!1s0x47900b29f69aa4c5:0xec34f49e3ca9ce2e!2sAhead+Health!8m2!3d47.3734618!4d8.5359742!9m1!1b1!16s%2Fg%2F11x1bl_tpp!3m5!1s0x47900b29f69aa4c5:0xec34f49e3ca9ce2e!8m2!3d47.3734618!4d8.5359742!16s%2Fg%2F11x1bl_tpp?entry=ttu"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Read Ahead Health reviews on Google"
              className="inline-flex items-center gap-1.5 rounded-full transition-opacity hover:opacity-80"
            >
              {Array.from({ length: 5 }, (_, i) => (
                <StarIcon key={i} />
              ))}
              <GoogleIcon width={18} height={18} className="ml-0.5" />
            </a>
          </div>
          <h1 className="type-display-lg text-white">{t(k.heroHeadline)}</h1>

          <p className="type-body-xl mx-auto mt-6 max-w-2xl text-white/70">
            {splitIntoSentences(t(k.heroSubtitle)).map((sentence, i) => (
              <span key={i} className="block">
                {sentence}
              </span>
            ))}
          </p>

          <div className="mx-auto mt-10 flex w-full max-w-md flex-col justify-center gap-3 sm:max-w-none sm:flex-row sm:items-center">
            <button
              type="button"
              onClick={() =>
                document
                  .getElementById("approach")
                  ?.scrollIntoView({ behavior: "smooth" })
              }
              className="type-body-md inline-flex w-full cursor-pointer items-center justify-center rounded-full bg-white px-8 py-3 font-medium text-neutral-900 transition-all hover:shadow-[inset_0_0_0_999px_rgba(0,0,0,0.04)] sm:w-auto"
            >
              {t(k.heroCta)}
            </button>
            <button
              type="button"
              onClick={() =>
                document
                  .getElementById("careers")
                  ?.scrollIntoView({ behavior: "smooth" })
              }
              className="type-body-md inline-flex w-full cursor-pointer items-center justify-center rounded-full border border-white/40 px-8 py-3 font-medium text-white transition-all hover:bg-white/10 sm:w-auto"
            >
              {t(k.heroOpenRoles)}
            </button>
          </div>
        </div>
      </section>

      {/* ── Problem / manifesto ─────────────────────────── */}
      <section className="pt-20 pb-10 md:pt-28 md:pb-14">
        <div className="mx-auto max-w-[1440px] px-4 md:px-10">
          <div className="grid gap-10 md:grid-cols-2 md:gap-20">
            <div>
              <p className="type-label-lg text-neutral-400">
                {t(k.problemLabel)}
              </p>
              <h2 className="type-display-sm mt-6 text-balance text-neutral-900">
                {splitIntoSentences(t(k.problemHeadline)).map((sentence, i) => (
                  <span key={i} className="inline text-balance md:block">
                    {sentence}{" "}
                  </span>
                ))}
              </h2>
              <p className="type-body-lg mt-6 text-neutral-600">
                {t(k.problemBody)}
              </p>
            </div>

            <div>
              <div className="ml-auto grid max-w-[560px] grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="type-body-lg bg-aubergine-900 flex aspect-square flex-col justify-end rounded-[32px] p-8 text-white">
                  <p className="type-display-lg mb-4 leading-none font-light">
                    <CountUp target={45} />%
                  </p>
                  <p>{t(k.problemStatDesc)}</p>
                  <p className="type-body-sm mt-3 text-white/60">
                    {t(k.problemStatSource)}
                  </p>
                </div>

                <div className="type-body-lg bg-light-green-400 flex aspect-square flex-col justify-end rounded-[32px] p-8 text-neutral-900">
                  <p className="type-display-lg mb-4 leading-none font-light">
                    <CountUp target={90} />%
                  </p>
                  <p>{t(k.problemStat2Text)}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Approach — 3 pillars (free-call-style cards) ──── */}
      <section id="approach" className="py-10 md:py-14">
        <div className="mx-auto max-w-[1440px] px-4 md:px-10">
          <div>
            <p className="type-label-lg text-center text-neutral-400">
              {t(k.approachLabel)}
            </p>
          </div>

          <div>
            <h2 className="type-display-md mt-4 text-center text-neutral-900">
              {t(k.approachHeadline)}
            </h2>
          </div>

          {(() => {
            const PILLARS = [
              {
                image: blobMediaUrl("/images/homepage/service-mri.jpeg"),
                title: t(k.pillar1Title),
                desc: t(k.pillar1Desc),
                ctaLabel: t(k.pillar1Cta),
                ctaHref: lp("/pricing"),
              },
              {
                image: blobMediaUrl("/images/how-it-works/hero-portrait.jpeg"),
                title: t(k.pillar2Title),
                desc: t(k.pillar2Desc),
                ctaLabel: t(k.pillar2Cta),
                ctaHref: lp("/locations"),
              },
              {
                image: blobMediaUrl("/images/how-it-works/hero-app.png"),
                title: t(k.pillar3Title),
                desc: t(k.pillar3Desc),
                ctaLabel: t(k.pillar3Cta),
                ctaHref: lp("/report"),
              },
            ]

            return (
              <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3">
                {PILLARS.map((pillar) => (
                  <div key={pillar.title}>
                    <div className="flex h-full flex-col rounded-[16px] bg-white p-3">
                      <div className="relative aspect-[4/3] w-full overflow-hidden rounded-[12px]">
                        <Image
                          src={pillar.image}
                          alt=""
                          fill
                          sizes="(min-width: 768px) 33vw, 100vw"
                          className="object-cover"
                        />
                      </div>
                      <div className="flex flex-1 flex-col p-3 pt-6">
                        <h3 className="type-title-md text-neutral-900">
                          {pillar.title}
                        </h3>
                        <p className="type-body-md mt-2 leading-relaxed text-neutral-500">
                          {pillar.desc}
                        </p>
                        <div className="mt-auto pt-4">
                          <Link href={pillar.ctaHref}>
                            <Button
                              variant="text"
                              className="group !px-0"
                              iconRight={
                                <ArrowRightIcon
                                  size={20}
                                  className="transition-transform duration-200 group-hover:translate-x-1"
                                />
                              }
                            >
                              {pillar.ctaLabel}
                            </Button>
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )
          })()}
        </div>
      </section>

      {/* ── Values — 6 cards ─────────────────────────────── */}
      <section className="py-10 md:py-14">
        <div className="mx-auto max-w-[1440px] px-4 md:px-10">
          <div>
            <p className="type-label-lg text-neutral-400">{t(k.valuesLabel)}</p>
          </div>

          <div>
            <h2 className="type-display-sm mt-4 text-neutral-900">
              {t(k.valuesHeading)}
            </h2>
          </div>

          {(() => {
            const VALUES = [
              {
                icon: (
                  <SwissShieldIcon size={28} className="text-neutral-900" />
                ),
                name: t(k.valueTrustedSidekickName),
                desc: t(k.valueTrustedSidekickDesc),
              },
              {
                icon: <LifestyleIcon size={28} className="text-neutral-900" />,
                name: t(k.valueAlwaysThereName),
                desc: t(k.valueAlwaysThereDesc),
              },
              {
                icon: <ChecklistIcon size={28} className="text-neutral-900" />,
                name: t(k.valueBetterEveryDayName),
                desc: t(k.valueBetterEveryDayDesc),
              },
              {
                icon: <ActionPlanIcon size={28} className="text-neutral-900" />,
                name: t(k.valueActionablyProactiveName),
                desc: t(k.valueActionablyProactiveDesc),
              },
              {
                icon: <ExerciseIcon size={28} className="text-neutral-900" />,
                name: t(k.valueGrittyName),
                desc: t(k.valueGrittyDesc),
              },
              {
                icon: (
                  <MapPinIcon
                    width={28}
                    height={28}
                    className="text-neutral-900"
                  />
                ),
                name: t(k.valueThinkBigName),
                desc: t(k.valueThinkBigDesc),
              },
            ]

            return (
              <div className="mt-12 grid grid-cols-1 gap-x-10 gap-y-12 md:grid-cols-2 lg:grid-cols-3">
                {VALUES.map((value) => (
                  <div key={value.name}>
                    {value.icon}
                    <h3 className="type-title-md mt-4 text-neutral-900">
                      {value.name}
                    </h3>
                    <p className="type-body-md mt-3 text-neutral-600">
                      {value.desc}
                    </p>
                  </div>
                ))}
              </div>
            )
          })()}
        </div>
      </section>

      {/* ── Team — 4 tiers ──────────────────────────────── */}
      <section className="bg-background px-6 py-10 md:py-14">
        <div className="mx-auto max-w-5xl">
          <div>
            <p className="type-label-lg text-neutral-400">{t(k.teamLabel)}</p>
          </div>

          <div>
            <h2 className="type-display-sm mt-4 text-neutral-900">
              {t(k.teamHeadline)}
            </h2>
          </div>

          <div>
            <p className="type-body-lg mt-4 max-w-3xl text-neutral-600">
              {t(k.teamSubtitle)}
            </p>
          </div>

          {/* Tier 1 — Leadership */}
          {(() => {
            const LEADERSHIP = [
              {
                name: "Nick Lenten",
                role: "CEO",
                title: t(k.leadershipNickTitle),
                photo: blobMediaUrl("/images/about/nick-lenten.png"),
                badge: "olive" as const,
                linkedin: "https://www.linkedin.com/in/nlenten/",
                logos: [
                  {
                    src: blobMediaUrl("/images/logos/google.svg"),
                    name: "Google",
                  },
                  {
                    src: blobMediaUrl("/images/logos/coolblue.svg"),
                    name: "Coolblue",
                  },
                  {
                    src: blobMediaUrl("/images/logos/suitsupply.svg"),
                    name: "Suitsupply",
                  },
                  { src: "/images/logos/everyman.svg", name: "Everyman" },
                ],
              },
              {
                name: "Sven Jaeschke, PhD",
                role: "CPO",
                title: t(k.leadershipSvenTitle),
                photo: blobMediaUrl("/images/about/sven-jaeschke.png"),
                badge: "olive" as const,
                linkedin: "https://www.linkedin.com/in/svenjaeschke/",
                logos: [
                  {
                    src: blobMediaUrl("/images/logos/oxford.svg"),
                    name: "Oxford",
                  },
                  { src: "/images/logos/deepspin.svg", name: "DeepSpin" },
                  {
                    src: blobMediaUrl("/images/logos/iqvia.svg"),
                    name: "IQVIA",
                  },
                ],
              },
              {
                name: "Dennis Hermann",
                role: "COO",
                title: t(k.leadershipDennisTitle),
                photo: blobMediaUrl("/images/about/dennis-hermann.png"),
                badge: "olive" as const,
                linkedin: "https://www.linkedin.com/in/dennishermann",
                logos: [
                  {
                    src: blobMediaUrl("/images/logos/kaia.svg"),
                    name: "Kaia Health",
                  },
                ],
              },
              {
                name: "Vega Vinsensius, PhD",
                role: "CTO",
                title: t(k.leadershipVegaTitle),
                photo: blobMediaUrl("/images/about/vega-vinsensius.png"),
                badge: "olive" as const,
                linkedin: "https://www.linkedin.com/in/vinsensius-vega/",
                logos: [
                  {
                    src: blobMediaUrl("/images/logos/eth.svg"),
                    name: "ETH Zurich",
                  },
                  {
                    src: blobMediaUrl("/images/logos/google.svg"),
                    name: "Google",
                  },
                ],
              },
            ]

            return (
              <div className="mt-10 grid grid-cols-2 gap-6 lg:grid-cols-4">
                {LEADERSHIP.map((person) => (
                  <div key={person.name} className="h-full">
                    <div className="flex h-full flex-col overflow-hidden rounded-[16px] bg-white">
                      <div className="relative aspect-[3/4] overflow-hidden">
                        <TeamPhoto src={person.photo} name={person.name} />
                        <span className="type-body-sm absolute top-3 left-3 rounded-full bg-white px-3 py-1 font-semibold tracking-wider text-neutral-900 uppercase">
                          {person.role}
                        </span>
                      </div>
                      <div className="flex flex-1 flex-col p-4">
                        <div className="flex items-start justify-between gap-2">
                          <p className="type-title-sm text-neutral-900">
                            {person.name}
                          </p>
                          <a
                            href={person.linkedin}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label={`${person.name} on LinkedIn`}
                            className="flex-shrink-0 text-neutral-400 transition-colors hover:text-[#0A66C2]" // ds-color-ignore: third-party / brand logo colour
                          >
                            <LinkedInIcon width={14} height={14} />
                          </a>
                        </div>
                        <p className="type-body-sm mt-0.5 text-neutral-500">
                          {person.title}
                        </p>
                        <div className="mt-auto flex h-[28px] flex-wrap items-center gap-2 overflow-hidden pt-3">
                          {person.logos.map((logo) => (
                            <CompanyLogo
                              key={logo.name}
                              src={logo.src}
                              name={logo.name}
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )
          })()}

          {/* Tier 2 — Medical founding partners */}
          {(() => {
            const MEDICAL_PARTNERS = [
              {
                name: "Prof. Dr. Olivio Donati",
                role: t(k.medicalPartnerRole),
                title: t(k.medicalPartnerTitle),
                photo: blobMediaUrl("/images/about/olivio-donati.jpg"),
                badge: "aubergine" as const,
                logos: [
                  { src: blobMediaUrl("/images/logos/uzh.svg"), name: "UZH" },
                  {
                    src: blobMediaUrl("/images/logos/octorad.png"),
                    name: "Octorad",
                  },
                  { src: "/images/logos/hirslanden.svg", name: "Hirslanden" },
                ],
              },
              {
                name: "Prof. Dr. Michael Fischer",
                role: t(k.medicalPartnerRole),
                title: t(k.medicalPartnerTitle),
                photo: blobMediaUrl("/images/about/michael-fischer.jpg"),
                badge: "aubergine" as const,
                logos: [
                  { src: blobMediaUrl("/images/logos/uzh.svg"), name: "UZH" },
                  {
                    src: blobMediaUrl("/images/logos/octorad.png"),
                    name: "Octorad",
                  },
                  { src: "/images/logos/hirslanden.svg", name: "Hirslanden" },
                ],
              },
            ]

            return (
              <div className="mt-10 grid grid-cols-2 gap-6 lg:grid-cols-4">
                {MEDICAL_PARTNERS.map((person, i) => (
                  <div
                    key={person.name}
                    className={i === 0 ? "lg:col-start-2" : undefined}
                  >
                    <div className="flex h-full flex-col overflow-hidden rounded-[16px] bg-white">
                      <div className="relative aspect-[3/4] overflow-hidden">
                        <TeamPhoto src={person.photo} name={person.name} />
                        {/* nowrap: the badge is shrink-to-fit over the photo,
                            so once German hyphenation applies site-wide
                            (AHD-10495) the lower min-content width of
                            "Gründungs­partner" would break it across three
                            lines instead of leaving it as one pill. */}
                        <span className="type-label-sm absolute top-3 left-3 rounded-full bg-[#3D1C51] px-3 py-1 font-semibold tracking-wider whitespace-nowrap text-white uppercase">
                          {person.role}
                        </span>
                      </div>
                      <div className="flex flex-1 flex-col p-4">
                        <p className="type-title-sm text-neutral-900">
                          {person.name}
                        </p>
                        <p className="type-body-sm mt-0.5 text-neutral-500">
                          {person.title}
                        </p>
                        <div className="mt-auto flex h-[28px] flex-wrap items-center gap-2 overflow-hidden pt-3">
                          {person.logos.map((logo) => (
                            <CompanyLogo
                              key={logo.name}
                              src={logo.src}
                              name={logo.name}
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )
          })()}

          {/* Tier 3 — Advisory board */}
          {(() => {
            const ADVISORS = [
              {
                name: "Dr. med. Anna Erat, PhD",
                credential: t(k.advisorCredential1),
                photo: blobMediaUrl("/images/doctors/erat.jpeg"),
              },
              {
                name: "Prof. Dr. med. Edouard Battegay",
                credential: t(k.advisorCredential4),
                photo:
                  "https://rc1mdjsplvzsekgk.public.blob.vercel-storage.com/battegay.jpg",
              },
              {
                name: "Prof. Dr. Benedikt Wiestler",
                credential: t(k.advisorCredential2),
                photo: blobMediaUrl("/images/doctors/wiestler.jpeg"),
              },
              {
                name: "Dr. Jacob J. Visser",
                credential: t(k.advisorCredential3),
                photo: blobMediaUrl("/images/doctors/visser.jpg"),
              },
            ]

            return (
              <div id="advisory-board" className="mt-14 scroll-mt-24">
                <div>
                  <p className="type-label-md mb-6 text-neutral-400">
                    {t(k.advisoryBoardLabel)}
                  </p>
                </div>
                <div>
                  <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 md:gap-12">
                    {ADVISORS.map((advisor) => (
                      <div
                        key={advisor.name}
                        className="flex items-center gap-3"
                      >
                        <div className="relative h-12 w-12 flex-shrink-0 overflow-hidden rounded-full">
                          <Image
                            src={advisor.photo}
                            alt={advisor.name}
                            fill
                            className="object-cover"
                          />
                        </div>
                        <div>
                          <p className="type-title-sm text-neutral-900">
                            {advisor.name}
                          </p>
                          <p className="type-body-sm text-neutral-500">
                            {advisor.credential}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )
          })()}

          {/* Tier 4 — Wider team */}
          {(() => {
            const WIDER_TEAM = [
              {
                name: "Katrine",
                fullName: "Katrine Singer",
                role: t(k.widerTeamClinicalOps),
                photo: blobMediaUrl("/images/about/team/katrine.jpg"),
              },
              {
                name: "Agata",
                fullName: "Dr. Agata Jagoda",
                role: t(k.widerTeamGpPrevention),
                photo: blobMediaUrl("/images/about/team/agata.webp"),
              },
              {
                name: "Hadrien",
                fullName: "Dr. Hadrien Komaromi",
                role: t(k.widerTeamGpPreventionM),
                photo: blobMediaUrl("/images/about/team/hadrien.webp"),
              },
              {
                name: "Laurent",
                fullName: "Laurent Meyer",
                role: t(k.widerTeamSoftwareEngineer),
                photo: blobMediaUrl("/images/about/team/laurent.jpg"),
              },
              {
                name: "David",
                fullName: "David Gaynor",
                role: t(k.widerTeamSoftwareEngineer),
                photo: blobMediaUrl("/images/about/team/david.jpg"),
              },
              {
                name: "Eugene",
                fullName: "Eugene Chorny",
                role: t(k.widerTeamProductDesigner),
                photo: blobMediaUrl("/images/about/team/eugene.jpg"),
              },
              {
                name: "Lydia",
                fullName: "Lydia",
                role: t(k.widerTeamHeadOfGrowth),
                photo: blobMediaUrl("/images/about/team/lydia.jpg"),
              },
              {
                name: "Laura",
                fullName: "Laura Leoty",
                role: t(k.widerTeamCustomerSuccess),
                photo: blobMediaUrl("/images/about/team/laura.jpg"),
              },
              {
                name: "Ferdinand",
                fullName: "Ferdinand Skaugerum",
                role: t(k.widerTeamChiefOfStaff),
                photo: blobMediaUrl("/images/about/team/ferdinand.jpg"),
              },
            ]

            return (
              <div className="mt-14">
                <div>
                  <p className="type-label-md mb-6 text-neutral-400">
                    {t(k.widerTeamLabel)}
                  </p>
                </div>
                <div>
                  <div className="grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-3 lg:grid-cols-4">
                    {WIDER_TEAM.map((member) => (
                      <div
                        key={member.name}
                        className="group relative -mx-2 flex cursor-default items-center gap-3 rounded-xl px-2 py-1.5 transition-colors duration-[250ms] hover:bg-white"
                      >
                        <Image
                          src={member.photo}
                          alt={member.fullName}
                          width={40}
                          height={40}
                          className="h-10 w-10 flex-shrink-0 rounded-full object-cover"
                        />
                        <div className="min-w-0">
                          <span className="type-body-sm group-hover:text-aubergine-900 block text-neutral-600 transition-colors duration-[250ms]">
                            {member.name}
                          </span>
                          {/* Wraps, like the name above it: in a two-column
                            grid on a phone this span is ~109px wide, and
                            "Huisarts, preventiespecialist" (151px) or
                            "Allgemeinmedizinerin, Präventionsexpertin" used to
                            be cut mid-word. The qualification is the point of
                            the line, so it gets a second line instead. */}
                          <span className="type-body-sm block text-neutral-400">
                            {member.role}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )
          })()}
        </div>
      </section>

      {/* ── Investors ───────────────────────────────────── */}
      <section className="py-10 md:py-14">
        <div className="mx-auto max-w-[1440px] px-4 md:px-10">
          <div>
            <p className="type-label-lg pl-8 text-neutral-400 md:pl-12">
              {t(k.investorsLabel)}
            </p>
          </div>

          <div>
            <h2 className="type-display-sm mt-4 pl-8 text-neutral-900 md:pl-12">
              {t(k.investorsHeadline)}
            </h2>
          </div>

          <div>
            <div className="mt-10 rounded-[16px] bg-white p-8 md:p-12">
              {/* Lead investor */}
              <Image
                src={blobMediaUrl("/images/logos/rtp-global.svg")}
                alt="RTP Global"
                width={320}
                height={64}
                className="h-16 w-auto object-contain object-left"
              />

              {/* VC row */}
              <div className="mt-6 flex items-center gap-10">
                <Image
                  src={blobMediaUrl("/images/logos/tinyvc.svg")}
                  alt="Tiny.vc"
                  width={100}
                  height={24}
                  className="h-6 w-auto object-contain object-left"
                />
                <Image
                  src={blobMediaUrl("/images/logos/pareto.png")}
                  alt="Pareto"
                  width={100}
                  height={24}
                  className="h-6 w-auto object-contain object-left"
                />
              </div>

              {/* Divider */}
              <div className="my-6 border-t border-neutral-100" />

              {/* Angels label */}
              <p className="type-label-md mb-4 text-neutral-400">
                {t(k.investorsAngelsLabel)}
              </p>

              {/* Angels grid */}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {ANGELS.map((angel) => (
                  <div key={angel.name}>
                    <div>
                      <p className="type-title-sm text-neutral-900">
                        {angel.name}
                      </p>
                      <p className="type-body-sm text-neutral-500">
                        {angel.affiliationKey
                          ? t(angel.affiliationKey)
                          : angel.affiliation}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Careers ─────────────────────────────────────── */}
      <section
        id="careers"
        className="bg-background scroll-mt-24 px-6 py-10 md:py-14"
      >
        <div className="mx-auto max-w-5xl">
          <div>
            <p className="type-label-lg text-center text-neutral-400">
              {t(k.careersLabel)}
            </p>
          </div>

          <div>
            <h2 className="type-display-md mt-4 text-center text-neutral-900">
              {t(k.careersHeadline)}
            </h2>
          </div>

          <div className="mt-10 space-y-3">
            {jobs.map((job) => (
              <div key={job.title}>
                <Link
                  href={lp("/careers/" + jobSlug(job))}
                  className="group flex flex-col gap-4 rounded-[16px] bg-white p-6 transition-shadow hover:shadow-[0_4px_20px_rgba(0,0,0,0.06)] sm:flex-row sm:items-center sm:justify-between"
                >
                  <div>
                    <p className="type-title-md text-neutral-900">
                      {job.title}
                    </p>
                    <p className="type-body-sm mt-1 text-neutral-500">
                      {translateJobType(job.type)} · {job.location}
                    </p>
                  </div>
                  <span
                    className={buttonVariants({ variant: "outline" })}
                    aria-hidden="true"
                  >
                    {t(k.careersSeeRole)}
                  </span>
                </Link>
              </div>
            ))}

            {/* Open application */}
            <div>
              <a
                href="mailto:nick@aheadhealth.com"
                className="flex flex-col gap-4 rounded-[16px] border border-dashed border-neutral-300 p-6 transition-colors hover:border-neutral-400 hover:bg-white/50 sm:flex-row sm:items-center sm:justify-between"
              >
                <div>
                  <p className="type-title-md text-neutral-900">
                    {t(k.careersOpenAppTitle)}
                  </p>
                  <p className="mt-1 text-sm text-neutral-500">
                    {t(k.careersOpenAppDesc)}
                  </p>
                </div>
                <span
                  className={buttonVariants({ variant: "outline" })}
                  aria-hidden="true"
                >
                  {t(k.careersOpenAppCta)}
                </span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ── Closing CTA ─────────────────────────────────── */}
      <div>
        <CtaBanner
          title={t(k.closingHeadline)}
          imageSrc={blobMediaUrl("/images/misc/advisory-bg.png")}
          primaryCta={{ label: t(k.closingCta), href: lp("/how-it-works") }}
          className="!min-h-[280px] md:!min-h-[400px]"
          overlayClassName="hidden"
        />
      </div>
    </>
  )
}
